import type { Dictionary } from './en.ts';

export const ru: Dictionary = {
  common: {
    tagline: 'голосовой переводчик в реальном времени',
    interfaceLanguage: 'Язык интерфейса',
  },

  login: {
    title: 'Вход',
    lede: 'Введите код доступа к демо. Этот браузер запомнит его на 12 часов.',
    submit: 'Войти',
    checking: 'Проверяем…',
    wrong: 'Код неверный. Проверьте его и попробуйте ещё раз.',
    failed: 'Не удалось войти. Попробуйте через минуту.',
  },

  home: {
    title: 'Разговор на двух языках',
    lede: 'Каждый говорит на своём языке и слышит перевод собеседника. Комната на двоих, без регистрации.',
    myLanguage: 'Мой язык',
    peerLanguage: 'Язык собеседника',
    accessCode: 'Код доступа к демо',
    signOut: 'Выйти',
    samePair: 'Выберите два разных языка — иначе переводить нечего.',
    create: 'Создать комнату',
    creating: 'Создаём…',
    createFailed: 'Не удалось создать комнату',

    readyTitle: 'Комната готова',
    youHear: (language: string) => `Вы слышите: ${language}.`,
    theyHear: (language: string) => `Собеседник слышит: ${language}.`,
    inviteLabel: 'Ссылка-приглашение — отправьте собеседнику',
    copy: 'Копировать',
    copied: 'Скопировано',
    inviteNote:
      'Ссылка действует 15 минут и пускает только одного человека. Не публикуйте её — она и есть пропуск в комнату.',
    sourceSubtitles: 'Показывать и оригинал',
    sourceSubtitlesOn:
      'Будет видно, что человек сказал на своём языке, рядом с переводом. Провайдер берёт за это отдельно, так что звонок обойдётся дороже.',
    sourceSubtitlesOff:
      'Показывается только перевод. Оригинал не расшифровывается, доплачивать не за что.',
    sourceSubtitlesReady: 'Оригинал тоже будет показан.',
    goToCheck: 'Перейти к проверке звука',
  },

  voice: {
    you: 'Вы',
    peer: 'Собеседник',
    language: 'Язык',
    provider: 'Озвучка',
    openaiNote: 'встроенный голос, звучит сразу',
    elevenlabsNote: 'голос чище, начинает после конца фразы',
    noVoices: 'для этого языка голосов нет',
    voice: 'Голос',
    builtIn: 'Встроенный голос OpenAI',
    loading: 'Загружаем голоса…',
    failed: 'Не удалось загрузить голоса. Попробуйте позже.',
    hintBuiltIn: 'Один голос на все языки, как раньше.',
    hintLibrary: 'Голос из библиотеки ElevenLabs.',
    groupVerified: 'Проверены для этого языка',
    groupOther: 'Другие голоса',
    accent: 'Этот голос не проверен для этого языка, возможен акцент.',
    noSample: 'У этого голоса нет образца',
    listen: 'Послушать',
    stop: 'Остановить',
    needVoice: 'Выберите голос для каждой стороны с ElevenLabs или верните ей OpenAI.',
  },

  join: {
    checking: 'Проверяем приглашение…',
    title: 'Приглашение в разговор',
    youWillHear: (language: string) => `Вы будете слышать: ${language}.`,
    theyHear: (language: string) => `Собеседник слышит: ${language}.`,
    connect: 'Подключиться',
    connecting: 'Подключаемся…',
    full: 'Комната заполнена.',
    failedTitle: 'Не получилось войти',
    createYourOwn: 'Создать свою комнату',
    noInvite: 'В ссылке нет приглашения. Попросите отправить её заново.',
    unavailable: 'Комната недоступна',
    joinFailed: 'Не удалось подключиться',
  },

  check: {
    title: 'Проверка звука',
    lede: 'Наденьте наушники: без них перевод попадёт обратно в микрофон и собеседник услышит эхо.',
    microphone: 'Микрофон',
    defaultMic: 'Микрофон по умолчанию',
    level: 'Уровень — скажите что-нибудь',
    applied: (features: string) => `Браузер применил: ${features}.`,
    appliedNone: 'без обработки',
    echoCancellation: 'эхоподавление',
    noiseSuppression: 'шумоподавление',
    autoGain: 'авто-громкость',
    measureNote: 'Замеры делайте в наушниках.',
    testSound: 'Проверить звук',
    testAgain: 'Проверить звук ещё раз',
    start: 'Всё хорошо, начать',
    starting: 'Подключаемся…',
    micDenied:
      'Доступ к микрофону запрещён. Разрешите его в настройках браузера и обновите страницу.',
    micNotFound: 'Микрофон не найден. Подключите его и обновите страницу.',
    micFailed: 'Не удалось открыть микрофон.',
  },

  call: {
    title: 'Разговор',
    timeLeft: (time: string) => `· осталось ${time}`,
    waitingPeer: 'Ждём собеседника. Отправьте ему ссылку-приглашение.',
    autoplayBlocked: 'Браузер заблокировал автовоспроизведение.',
    enableSound: 'Включить звук',
    lagging:
      'Перевод отстаёт. Можно перезапустить переводчика — незавершённый фрагмент будет потерян.',
    translatorStopped: (reason: string) =>
      `Перевод остановился: ${reason}. Разговор продолжается — перезапустите переводчика.`,
    translationVolume: 'Громкость перевода',
    hearOriginal: 'Слышать оригинал',
    muteMic: 'Выключить микрофон',
    unmuteMic: 'Включить микрофон',
    restart: 'Перезапустить перевод',
    asleep:
      'Минуту никто не говорил, и перевод встал на паузу, чтобы не тратить деньги. Он включится сам, когда собеседник заговорит.',
    wake: 'Включить сейчас',
    leave: 'Выйти',
    peerMuted: 'У собеседника выключен микрофон',

    metricConnect: 'Запуск перевода',
    metricFirst: 'Первый перевод',
    metricLag: 'Оценка отставания',
    metricRtt: 'RTT до провайдера',
    metricVoiceDelay: 'Задержка озвучки',
    metricCost: 'Стоимость, оценка',
    costBreakdown: (openai: string, voice: string, credits: number) =>
      `OpenAI ${openai} · ElevenLabs ${voice} · кредитов: ${credits}`,
    costNote: 'По ценам из настроек сервера, а не по счёту провайдеров.',
    metricVoiceLead: 'ElevenLabs против OpenAI, в среднем',
    voiceLeadEarlier: (seconds: string) => `на ${seconds} с раньше`,
    voiceLeadLater: (seconds: string) => `на ${seconds} с позже`,
    iHear: (voice: string) => `Я слышу: ${voice}`,
    peerHears: (voice: string) => `Собеседник слышит: ${voice}`,
    voiceFellBack: 'OpenAI (ElevenLabs не ответил)',

    endedTitle: 'Разговор завершён',
    endedNewRoom: 'Для нового разговора создайте новую комнату.',
    newRoom: 'Новая комната',
    rateTitle: 'Оцените разговор',
  },

  closed: {
    'peer-left': 'Собеседник завершил разговор.',
    'time-limit': 'Время демонстрации вышло.',
    'peer-lost': 'Собеседник пропал со связи дольше допустимого.',
    'server-shutdown': 'Сервер перезапускается.',
    expired: 'Комната закрылась по таймауту.',
    unknown: 'Соединение закрыто.',
    signalLost: 'Связь с сервером потеряна. Создайте новую комнату.',
  },

  status: {
    serverConnecting: 'Сервер: подключаемся',
    serverOpen: 'Сервер: на связи',
    serverReconnecting: 'Сервер: переподключение',
    serverClosed: 'Сервер: отключён',

    peerWaiting: 'Собеседник: ожидание',
    peerConnecting: 'Собеседник: соединяемся',
    peerConnected: 'Собеседник: на связи',
    peerReconnecting: 'Собеседник: восстановление',
    peerFailed: 'Собеседник: нет связи',
    peerGone: 'Собеседник: вышел',

    translatorIdle: 'Перевод для меня: не запущен',
    translatorConnecting: 'Перевод для меня: запускается',
    translatorReady: 'Перевод для меня: работает',
    translatorReconnecting: 'Перевод для меня: восстановление',
    translatorFailed: 'Перевод для меня: ошибка',
    translatorClosed: 'Перевод для меня: остановлен',
    translatorAsleep: 'Перевод для меня: на паузе',

    peerTranslatorReady: 'Перевод для собеседника: работает',
    peerTranslatorWaiting: 'Перевод для собеседника: ждём',
    peerTranslatorAsleep: 'Перевод для собеседника: на паузе',
    relayed: 'Маршрут: через TURN',
  },

  subtitles: {
    them: 'Собеседник',
    you: 'Вы',
    themEmpty: 'Собеседник ещё ничего не сказал',
    youEmpty: 'Скажите что-нибудь — здесь появится ваша речь и её перевод',
  },

  rating: {
    accuracy: 'Точность перевода',
    voice: 'Естественность голоса',
    comfort: 'Удобство разговора',
    send: 'Отправить оценки',
    thanks: 'Спасибо — оценки записаны.',
  },

  errors: {
    no_credit: 'на счёте OpenAI закончились средства — пополните баланс',
    rate_limited: 'сервис перевода ограничивает частоту запросов — попробуйте через минуту',
    unconfigured: 'на сервере не настроен ключ сервиса перевода',
    unreachable: 'сервер не смог достучаться до сервиса перевода',
    offline: 'не удалось связаться с сервером — проверьте соединение',
    session_expired: 'сессия перевода истекла — перезапустите перевод',
    room_closed: 'эта комната уже закрыта',
    bad_offer: 'браузер сформировал некорректное предложение соединения',
    provider_refused: (status: number) => `сервис перевода отклонил соединение (${status})`,
    unknown: 'не удалось запустить перевод',
  },

  banner: {
    providerUnconfigured:
      'На сервере не настроен ключ провайдера перевода. Комнату создать можно, но перевода не будет.',
  },
};
